import {
  Component,
  type OnInit,
  inject,
  afterNextRender,
  Injector,
  runInInjectionContext,
  ChangeDetectionStrategy,
  computed,
  signal,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import type { MenuItemType } from '@shared/types/layout';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { scrollToElement } from '@shared/utils/layout-utils';
import { menuItems } from '@layouts/components/data';
import { LayoutStoreService } from '@core/services/layout-store.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { TranslocoService } from '@jsverse/transloco';
import type { Params } from '@angular/router';
import { APP_MENU_NAVIGATION, type AppMenuNavigation } from './app-menu-navigation';
import { AppMenuItemComponent } from './app-menu-item/app-menu-item.component';
import { AppMenuItemWithChildrenComponent } from './app-menu-item-with-children/app-menu-item-with-children.component';

const MENU_ICON_KEYS = new Set([
  'orders',
  'claimsReturns',
  'pspsettlements',
  'bankledger',
  'wallet',
  'partners',
  'products',
  'person',
  'logs',
]);

@Component({
  selector: 'app-menu',
  imports: [AppMenuItemComponent, AppMenuItemWithChildrenComponent],
  templateUrl: './app-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: APP_MENU_NAVIGATION, useExisting: AppMenuComponent }],
})
export class AppMenuComponent implements OnInit, AppMenuNavigation {
  router = inject(Router);
  layout = inject(LayoutStoreService);
  private readonly currentUserService = inject(CurrentUserService);
  private injector = inject(Injector);
  private readonly translocoService = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private isDestroyed = false;

  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  private readonly rawMenuItems = signal<MenuItemType[]>(menuItems);

  // Signal for current router URL to trigger change detection
  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly menuItems = computed(() => {
    const lang = this.activeLang();
    // Access currentUrl to make this computed reactive to URL changes
    this.currentUrl();
    this.currentUserService.userPrivileges();
    this.currentUserService.merchantId();
    const filtered = this.filterMenuItemsByPrivilege(this.rawMenuItems());
    return this.translateMenuItems(filtered, lang);
  });

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.expandActivePaths(this.menuItems());
        this.cdr.markForCheck();
        runInInjectionContext(this.injector, () => {
          afterNextRender(() => {
            if (!this.isDestroyed) {
              this.scrollToActiveLink();
            }
          });
        });
      });

    this.expandActivePaths(this.menuItems());
    runInInjectionContext(this.injector, () => {
      afterNextRender(() => {
        if (!this.isDestroyed) {
          this.scrollToActiveLink();
        }
      });
    });
  }

  private translateMenuItems(items: MenuItemType[], lang: string): MenuItemType[] {
    return items.map((item) => {
      const translatedItem: MenuItemType = {
        ...item,
        label: this.translocoService.translate(item.label, {}, lang),
      };

      if (item.badge != null) {
        translatedItem.badge = {
          ...item.badge,
          text: this.translocoService.translate(item.badge.text, {}, lang),
        };
      }

      if (item.children != null && item.children.length > 0) {
        translatedItem.children = this.translateMenuItems(item.children, lang);
      }

      return translatedItem;
    });
  }

  private filterMenuItemsByPrivilege(items: MenuItemType[]): MenuItemType[] {
    const withFilteredChildren = items
      .map((item) => {
        if (item.children != null && item.children.length > 0) {
          const children = this.filterMenuItemsByPrivilege(item.children);
          if (children.length === 0) {
            return null;
          }
          return { ...item, children };
        }
        return item;
      })
      .filter((item): item is MenuItemType => item !== null);

    const merchantId = this.currentUserService.merchantId();

    const visible = withFilteredChildren.filter((item) => {
      if (item.isTitle === true) {
        return true;
      }
      const hiddenForMerchantIds = item.hiddenForMerchantIds;
      if (
        merchantId !== null &&
        hiddenForMerchantIds !== undefined &&
        hiddenForMerchantIds.length > 0 &&
        hiddenForMerchantIds.includes(merchantId)
      ) {
        return false;
      }
      const req = item.requiredPrivileges;
      if (
        req !== undefined &&
        req.length > 0 &&
        !this.currentUserService.satisfiesAnyRoutePrivilege(req)
      ) {
        return false;
      }
      return true;
    });

    return this.removeOrphanSectionTitles(visible);
  }

  private removeOrphanSectionTitles(items: MenuItemType[]): MenuItemType[] {
    const result: MenuItemType[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item === undefined) {
        continue;
      }
      if (item.isTitle !== true) {
        result.push(item);
        continue;
      }
      let hasFollowingContent = false;
      for (let k = i + 1; k < items.length; k += 1) {
        const next = items[k];
        if (next === undefined) {
          continue;
        }
        if (next.isTitle === true) {
          break;
        }
        hasFollowingContent = true;
        break;
      }
      if (hasFollowingContent) {
        result.push(item);
      }
    }
    return result;
  }

  hasSubMenu(item: MenuItemType): boolean {
    return item.children != null && item.children.length > 0;
  }

  expandActivePaths(items: MenuItemType[]): void {
    // First collapse all menus
    this.collapseAllMenus(items);
    // Then expand only the active path
    this.expandActivePathRecursive(items);
  }

  private expandActivePathRecursive(items: MenuItemType[]): void {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        if (this.isChildActive(item)) {
          item.isCollapsed = false;
          this.expandActivePathRecursive(item.children ?? []);
        }
      }
    }
  }

  toggleMenu(clickedItem: MenuItemType): void {
    // Check current state before collapsing
    const wasCollapsed = clickedItem.isCollapsed ?? true;

    // Collapse all menu items
    this.collapseAllMenus(this.menuItems());

    // If item was collapsed, expand it. If it was expanded, leave it collapsed
    clickedItem.isCollapsed = !wasCollapsed;
  }

  private collapseAllMenus(items: MenuItemType[]): void {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        item.isCollapsed = true;
        this.collapseAllMenus(item.children ?? []);
      }
    }
  }

  isChildActive(item: MenuItemType): boolean {
    // Access currentUrl to make this reactive
    this.currentUrl();
    if (item.url != null && this.isUrlActive(item.url)) return true;
    if (item.children == null) return false;
    return item.children.some((child: MenuItemType) => this.isChildActive(child));
  }

  isActive(item: MenuItemType): boolean {
    // Access currentUrl to make this reactive
    this.currentUrl();
    if (item.url == null) return false;
    return this.isUrlActive(item.url);
  }

  shouldHighlightParent(item: MenuItemType): boolean {
    // Access currentUrl to make this reactive
    this.currentUrl();
    // If parent has no children, highlight if active
    if (!this.hasSubMenu(item)) {
      return this.isActive(item);
    }
    // If parent has children, highlight only if parent itself is active (not a child)
    if (item.url != null && this.isUrlActive(item.url)) {
      return true;
    }
    // Don't highlight parent if any child is active
    return false;
  }

  private isUrlActive(url: string): boolean {
    const parsed = this.parseUrl(url);
    const currentUrl = this.router.url.split('?')[0] ?? '';
    const currentQueryParams = this.router.parseUrl(this.router.url).queryParams;

    // If menu URL has query params, use exact matching
    if (parsed.queryParams != null && Object.keys(parsed.queryParams).length > 0) {
      if (currentUrl !== parsed.path) return false;
      return Object.keys(parsed.queryParams).every(
        (key) => currentQueryParams[key] === parsed.queryParams?.[key],
      );
    }

    // If menu URL has no query params, use path matching (ignore current URL query params)
    // So /orders is active for both /orders and /orders?status=active&page=1
    // This prevents /orders from matching /orders-rejected
    if (currentUrl === parsed.path) {
      return true;
    }

    // Check if current URL starts with menu path followed by "/"
    if (currentUrl.startsWith(`${parsed.path}/`)) {
      // "/merchants" list must not stay active on "/merchants/display-order" (separate menu entry)
      if (parsed.path === '/merchants' && currentUrl.startsWith('/merchants/display-order')) {
        return false;
      }
      return true;
    }

    return false;
  }

  parseUrl(url: string): { path: string; queryParams: Params | null } {
    const parts = url.split('?');
    const path = parts[0] ?? '';
    const queryString = parts[1];

    if (queryString == null) {
      return { path, queryParams: null };
    }

    const queryParams: Params = {};
    const params = new URLSearchParams(queryString);
    params.forEach((value, key) => {
      queryParams[key] = value;
    });

    return { path, queryParams };
  }

  getRouterLink(item: MenuItemType): string[] {
    if (item.url == null) return [];
    const parsed = this.parseUrl(item.url);
    return [parsed.path];
  }

  getQueryParams(item: MenuItemType): Params | null {
    if (item.url == null) return null;
    const parsed = this.parseUrl(item.url);
    return parsed.queryParams;
  }

  scrollToActiveLink(): void {
    const activeItem = document.querySelector('[data-active-link="true"]') as HTMLElement;
    const scrollContainer = document.querySelector(
      '#sidenav .simplebar-content-wrapper',
    ) as HTMLElement;

    if (activeItem != null && scrollContainer != null) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const offset = itemRect.top - containerRect.top - window.innerHeight * 0.4;

      scrollToElement(scrollContainer, scrollContainer.scrollTop + offset, 500);
    }
  }

  getIconSrc(item: MenuItemType): string | null {
    if (item.icon == null || !MENU_ICON_KEYS.has(item.icon)) {
      return null;
    }

    return `assets/icons/menu/${item.icon}.svg`;
  }
}
